import {
  calculateDownloadManagerDockTransform,
  type DownloadManagerDockBounds,
  type DownloadManagerDockCorner,
  type DownloadManagerDockSafeInsets,
} from '@/lib/download-manager-docking';
import type { VoiceToolActivity } from '@/lib/voice-tool-activity';

export type FloatingVoiceDockPosition =
  | DownloadManagerDockCorner
  | 'bottom-center';

export function getFloatingVoiceDockDefaultPosition(
  isSourceChatVisible: boolean
): FloatingVoiceDockPosition {
  return isSourceChatVisible ? 'bottom-center' : 'bottom-right';
}

export type FloatingVoiceDockStatus =
  | 'idle'
  | 'connecting'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'error';

export function getFloatingVoiceToolActivity({
  status,
  activity,
  isEnding,
  isPresent,
}: {
  status: FloatingVoiceDockStatus;
  activity?: VoiceToolActivity | null;
  isEnding?: boolean;
  isPresent: boolean;
}): VoiceToolActivity | null {
  if (
    isEnding ||
    !isPresent ||
    status === 'idle' ||
    status === 'connecting' ||
    status === 'error'
  )
    return null;
  return activity ?? null;
}

export function getFloatingVoiceCaptionMotion(
  dragging: boolean,
  reducedMotion: boolean
): { opacity: number; y: number } {
  return {
    opacity: dragging ? 0 : 1,
    y: dragging && !reducedMotion ? -8 : 0,
  };
}

export function getVoiceDockStatusKey(
  status: FloatingVoiceDockStatus,
  micMuted: boolean,
  pttActive: boolean,
  inputMode: 'vad' | 'ptt'
):
  | 'muted'
  | 'connecting'
  | 'callError'
  | 'releaseToSend'
  | 'holdSpace'
  | 'listening'
  | 'thinking'
  | 'speaking' {
  if (status === 'error') return 'callError';
  if (status === 'connecting' || status === 'idle') return 'connecting';
  if (micMuted) return 'muted';
  if (pttActive) return 'releaseToSend';
  if (status === 'thinking') return 'thinking';
  if (status === 'speaking') return 'speaking';
  if (inputMode === 'ptt') return 'holdSpace';
  return 'listening';
}

export function clampFloatingVoiceDockPoint(
  point: { x: number; y: number },
  boundary: { width: number; height: number },
  panel: { width: number; height: number },
  safeInsets?: DownloadManagerDockSafeInsets
): { x: number; y: number } {
  const geometry = {
    viewportWidth: boundary.width,
    viewportHeight: boundary.height,
    panelWidth: panel.width,
    panelHeight: panel.height,
    margin: 16,
    safeInsets,
  };
  const minimum = calculateDownloadManagerDockTransform('top-left', geometry);
  const maximum = calculateDownloadManagerDockTransform(
    'bottom-right',
    geometry
  );
  return {
    x: Math.max(minimum.x, Math.min(maximum.x, point.x)),
    y: Math.max(minimum.y, Math.min(maximum.y, point.y)),
  };
}

export type FloatingVoiceDockGeometry = {
  boundary: DownloadManagerDockBounds;
  panel: { width: number; height: number };
  obstacle?: DownloadManagerDockBounds | null;
  safeInsets?: DownloadManagerDockSafeInsets;
  margin?: number;
  gap?: number;
};

function overlaps(
  a: DownloadManagerDockBounds,
  b: DownloadManagerDockBounds,
  gap: number
): boolean {
  return (
    a.left < b.left + b.width + gap &&
    a.left + a.width + gap > b.left &&
    a.top < b.top + b.height + gap &&
    a.top + a.height + gap > b.top
  );
}

function cornerAlternatives(
  corner: DownloadManagerDockCorner
): DownloadManagerDockCorner[] {
  const vertical = corner.startsWith('top') ? 'top' : 'bottom';
  const horizontal = corner.endsWith('left') ? 'left' : 'right';
  const otherVertical = vertical === 'top' ? 'bottom' : 'top';
  const otherHorizontal = horizontal === 'left' ? 'right' : 'left';
  return [
    corner,
    `${vertical}-${otherHorizontal}`,
    `${otherVertical}-${horizontal}`,
    `${otherVertical}-${otherHorizontal}`,
  ];
}

export function getFloatingVoiceDockTarget(
  preferred: FloatingVoiceDockPosition,
  geometry: FloatingVoiceDockGeometry
): { corner: FloatingVoiceDockPosition; x: number; y: number } {
  const alternatives: FloatingVoiceDockPosition[] =
    preferred === 'bottom-center'
      ? [preferred, 'bottom-right', 'bottom-left', 'top-right', 'top-left']
      : cornerAlternatives(preferred);

  const candidates = alternatives.map((corner) => {
    const input = {
      viewportWidth: geometry.boundary.width,
      viewportHeight: geometry.boundary.height,
      panelWidth: geometry.panel.width,
      panelHeight: geometry.panel.height,
      safeInsets: geometry.safeInsets,
      margin: geometry.margin ?? 16,
    };
    const rightPoint = calculateDownloadManagerDockTransform(
      corner === 'bottom-center' ? 'bottom-right' : corner,
      input
    );
    const leftPoint =
      corner === 'bottom-center'
        ? calculateDownloadManagerDockTransform('bottom-left', input)
        : null;
    const point =
      corner === 'bottom-center'
        ? {
            x: Math.max(
              leftPoint?.x ?? 0,
              Math.min(
                rightPoint.x,
                (geometry.boundary.width - geometry.panel.width) / 2
              )
            ),
            y: rightPoint.y,
          }
        : rightPoint;
    return { corner, ...point };
  });
  const firstCandidate = candidates[0];
  if (!firstCandidate) throw new Error('Voice dock requires a position');
  const obstacle = geometry.obstacle;
  if (!obstacle) return firstCandidate;

  return (
    candidates.find(
      (candidate) =>
        !overlaps(
          {
            left: geometry.boundary.left + candidate.x,
            top: geometry.boundary.top + candidate.y,
            width: geometry.panel.width,
            height: geometry.panel.height,
          },
          obstacle,
          geometry.gap ?? 12
        )
    ) ?? firstCandidate
  );
}
